import React from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  IconButton,
  Paper,
  Typography,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Link,
  Dialog,
  DialogContent,
  Tooltip,
  TextField,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  DialogTitle,
  DialogActions,
  MenuItem,
  TablePagination
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import QrCode from 'react-qr-code';
import {
  useProjectStore
} from '../store/projectStore';
import {
  useLineStore
} from '../store/lineStore';
import {
  useIssueStore
} from '../store/issueStore';
import {
  ProjectFormDialog
} from '../components/ProjectFormDialog';
import {
  format
} from 'date-fns';
import {
  Breadcrumbs
} from '../components/Breadcrumbs';
import {
  getImageUrl,
  loadImageWithFallback
} from '../utils/imageUrl';
import * as XLSX from 'xlsx';
import ExcelJS from 'exceljs';

export default function ProjectDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const {
    currentProject,
    isLoading: projectLoading,
    error: projectError,
    fetchProject,
    updateProject,
  } = useProjectStore();

  const {
    lines,
    isLoading: linesLoading,
    error: linesError,
    fetchLines,
  } = useLineStore();

  const {
    issues,
    loading: issuesLoading,
    error: issuesError,
    fetchIssues,
    deleteIssue,
    clearIssues,
  } = useIssueStore();
  
  // Track current project ID to detect changes
  const [currentProjectId, setCurrentProjectId] = React.useState<string | undefined>(id);

  const [projectFormOpen, setProjectFormOpen] = React.useState(false);
  const [selectedImage, setSelectedImage] = React.useState<string | null>(null);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [selectedIssues, setSelectedIssues] = React.useState<number[]>([]);
  const [selectAll, setSelectAll] = React.useState(false);
  const [showFilters, setShowFilters] = React.useState(false);
  const [filterApplied, setFilterApplied] = React.useState(false);
  const [shareDialogOpen, setShareDialogOpen] = React.useState(false);
  
  // Pagination
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  
  // Sorting
  type Order = 'asc' | 'desc';
  type OrderBy = 'id' | 'type' | 'rank' | 'completion_date' | 'status' | 'progress';
  
  const [order, setOrder] = React.useState<Order>('asc');
  const [orderBy, setOrderBy] = React.useState<OrderBy>('id');

  // Handle pagination
  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Filter issues based on search term and selection
  const filteredIssues = React.useMemo(() => {
    if (!issues) return [];
    
    // First apply search filter
    const searchFiltered = issues.filter(issue => {
      const searchLower = searchTerm.toLowerCase();
      return (
        (issue.id.toString().includes(searchLower)) ||
        (issue.type?.toLowerCase().includes(searchLower)) ||
        (issue.rank?.toLowerCase().includes(searchLower)) ||
        (issue.line_name?.toLowerCase().includes(searchLower)) ||
        (issue.part_number?.toLowerCase().includes(searchLower)) ||
        (issue.part_name?.toLowerCase().includes(searchLower)) ||
        (issue.problem?.toLowerCase().includes(searchLower)) ||
        (issue.counter_measure?.toLowerCase().includes(searchLower)) ||
        (issue.final_counter_measure?.toLowerCase().includes(searchLower)) ||
        (issue.created_by_name?.toLowerCase().includes(searchLower)) ||
        (issue.pic_name?.toLowerCase().includes(searchLower)) ||
        (issue.status?.toLowerCase().includes(searchLower))
      );
    });
    
    // Then apply selection filter if it's active
    let result = searchFiltered;
    if (filterApplied && selectedIssues.length > 0) {
      result = result.filter(issue => selectedIssues.includes(issue.id));
    }
    
    // Apply sorting
    result = [...result].sort((a, b) => {
      const isAsc = order === 'asc';
      
      switch (orderBy) {
        case 'id':
          return isAsc ? a.id - b.id : b.id - a.id;
        case 'type':
          return isAsc 
            ? (a.type || '').localeCompare(b.type || '')
            : (b.type || '').localeCompare(a.type || '');
        case 'rank':
          return isAsc 
            ? (a.rank || '').localeCompare(b.rank || '')
            : (b.rank || '').localeCompare(a.rank || '');
        case 'completion_date':
          // Handle null/undefined dates
          if (!a.completion_date && !b.completion_date) return 0;
          if (!a.completion_date) return isAsc ? 1 : -1;
          if (!b.completion_date) return isAsc ? -1 : 1;
          return isAsc 
            ? new Date(a.completion_date).getTime() - new Date(b.completion_date).getTime()
            : new Date(b.completion_date).getTime() - new Date(a.completion_date).getTime();
        case 'status':
          return isAsc 
            ? (a.status || '').localeCompare(b.status || '')
            : (b.status || '').localeCompare(a.status || '');
        case 'progress':
          const progressA = getStatusPercentage(a.status);
          const progressB = getStatusPercentage(b.status);
          return isAsc ? progressA - progressB : progressB - progressA;
        default:
          return 0;
      }
    });
    
    return result;
  }, [issues, searchTerm, filterApplied, selectedIssues, order, orderBy]);

  // Pagination slice
  const paginatedIssues = React.useMemo(() => {
    return filteredIssues.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );
  }, [filteredIssues, page, rowsPerPage]);

  // Handle select all checkbox
  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectAll(event.target.checked);
    if (event.target.checked) {
      setSelectedIssues(filteredIssues.map(issue => issue.id));
    } else {
      setSelectedIssues([]);
    }
  };

  // Handle individual row selection
  const handleSelectRow = (issueId: number, event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setSelectedIssues(prev => {
      if (prev.includes(issueId)) {
        return prev.filter(id => id !== issueId);
      } else {
        return [...prev, issueId];
      }
    });
  };

  // Check if all filtered issues are selected
  React.useEffect(() => {
    if (filteredIssues.length > 0 && selectedIssues.length === filteredIssues.length) {
      setSelectAll(true);
    } else {
      setSelectAll(false);
    }
  }, [selectedIssues, filteredIssues]);

  // Function to export project details and BIRA summary to Excel using ExcelJS
  const exportToExcelWithExcelJS = async () => {
    if (!currentProject || !issues) return;

    try {
      console.log('Starting Excel export with ExcelJS...');
      console.log(`Exporting ${issues.length} issues`);
      
      // Try using XLSX library instead of ExcelJS since it might be more compatible
      const workbook = XLSX.utils.book_new();
      
      // Create Project Details worksheet
      const projectDetailsData = [
        ['Project Details'],
        ['Name', currentProject.name],
        ['Code', currentProject.code || ''],
        ['Lines Count', currentProject.lines_count],
        ['Issues Count', currentProject.issues_count],
        ['Created On', format(new Date(currentProject.created_on), 'PPP') + ' ' + format(new Date(currentProject.created_on), 'p')],
      ];
      
      const projectSheet = XLSX.utils.aoa_to_sheet(projectDetailsData);
      
      // Add column widths
      projectSheet['!cols'] = [
        { wch: 15 },  // A
        { wch: 40 },  // B
      ];
      
      XLSX.utils.book_append_sheet(workbook, projectSheet, 'Project Details');
      
      // Create BIRA Summary worksheet
      const headers = [
        'No',
        'Type of Issue',
        'Rank',
        'Line',
        'Part No',
        'Part Name',
        'Problem Description',
        'Photos',
        'Counter Measure Request',
        'Final Counter Measure',
        'Issuer',
        'Issuer Date',
        'PIC',
        'Reviewer',
        'Approver',
        'Due Date',
        'Status',
        'Progress (%)'
      ];
      
      // Filter issues based on selection or filter
      let issuesToExport = issues;
      
      // If filter is applied, use only selected issues
      if (filterApplied && selectedIssues.length > 0) {
        issuesToExport = issues.filter(issue => selectedIssues.includes(issue.id));
      }
      // If no filter but search term exists, use filtered issues
      else if (searchTerm) {
        issuesToExport = filteredIssues;
      }
      
      console.log(`Exporting ${issuesToExport.length} filtered issues to Excel`);
      
      // Prepare data rows
      const biraData = issuesToExport.map(issue => [
        issue.id,
        formatWord(issue.type),
        formatWord(issue.rank),
        issue.line_name || '',
        issue.part_number || '',
        issue.part_name || '',
        issue.problem,
        issue.photos && issue.photos.length > 0 ? 'Yes' : 'No',  // Simple text for photos
        issue.counter_measure || '',
        issue.final_counter_measure || '',
        issue.created_by_name,
        formatDate(new Date(issue.created_on)),
        issue.pic_name || '',
        issue.reviewer_pic_name || '',
        issue.approver_pic_name || '',
        issue.completion_date ? formatDate(new Date(issue.completion_date)) : '',
        formatWord(issue.status),
        Math.round(getStatusPercentage(issue.status) * 100) + '%'
      ]);
      
      // Create the BIRA sheet with headers and data
      const biraSheet = XLSX.utils.aoa_to_sheet([headers, ...biraData]);
      
      // Set column widths for better readability
      biraSheet['!cols'] = [
        { wch: 5 },    // No
        { wch: 15 },   // Type
        { wch: 10 },   // Rank
        { wch: 15 },   // Line
        { wch: 15 },   // Part No
        { wch: 20 },   // Part Name
        { wch: 40 },   // Problem
        { wch: 15 },   // Photos
        { wch: 40 },   // Counter Measure
        { wch: 40 },   // Final Counter Measure
        { wch: 20 },   // Issuer
        { wch: 15 },   // Issuer Date
        { wch: 20 },   // PIC
        { wch: 20 },   // Reviewer
        { wch: 20 },   // Approver
        { wch: 15 },   // Due Date
        { wch: 15 },   // Status
        { wch: 10 }    // Progress
      ];
      
      // Add the BIRA sheet to the workbook
      XLSX.utils.book_append_sheet(workbook, biraSheet, 'BIRA Summary');
      
      // Save the Excel file
      const projectCode = currentProject.code ? currentProject.code : '';
      const projectName = currentProject.name.replace(/\s+/g, '_');
      const dateTimeStr = format(new Date(), 'yyyyMMdd_HHmmss');
      const fileName = projectCode 
        ? `${projectCode}_${projectName}_bira_summary_${dateTimeStr}.xlsx`
        : `${projectName}_bira_summary_${dateTimeStr}.xlsx`;
      
      console.log(`Saving Excel file as: ${fileName}`);
      
      // Write the file
      XLSX.writeFile(workbook, fileName);
      
      console.log('Excel export completed successfully');
      
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      alert('Failed to export to Excel. Please try again.');
    }
  };

  // Clear the applied filter
  const clearAppliedFilter = () => {
    setFilterApplied(false);
  };
  
  // Toggle filter mode
  const toggleFilters = () => {
    setShowFilters(!showFilters);
    if (showFilters) {
      // Clear selections when exiting filter mode
      setSelectedIssues([]);
      setSelectAll(false);
      setFilterApplied(false);
    }
  };

  // Navigate to issue detail
  const navigateToIssueDetail = (issueId: number) => {
    navigate(`/projects/${id}/issues/${issueId}/detail`);
  };

  React.useEffect(() => {
    if (id) {
      // Clear issues immediately when project ID changes to prevent showing stale data
      if (currentProjectId !== id) {
        clearIssues();
        setCurrentProjectId(id);
        // Reset pagination and selections
        setPage(0);
        setSelectedIssues([]);
        setSelectAll(false);
        setSearchTerm('');
        setFilterApplied(false);
      }
      
      fetchProject(parseInt(id, 10));
      fetchLines({ project_id: parseInt(id, 10) });
      fetchIssues({ project_id: parseInt(id, 10) });
    }
  }, [id, currentProjectId, fetchProject, fetchLines, fetchIssues, clearIssues]);

  const handleProjectFormSubmit = async (data: any) => {
    if (currentProject) {
      await updateProject(currentProject.id, data);
    }
  };

  const handleCreateIssueClick = () => {
    navigate(`/projects/${id}/issues/new`);
  };
  const handleDataAnalysisClick = () => {
    navigate(`/projects/${id}/data-analysis`);
  };
  const handleLinesClick = () => {
    navigate(`/projects/${id}/lines`);
  };

  const handleDetailIssueClick = (issue: any) => {
    navigate(`/projects/${id}/issues/${issue.id}/detail`);
  };

  const getStatusPercentage = (status: string) => {
    switch (status) {
      case 'waiting_response': return 0.25;
      case 'waiting_review': return 0.5;
      case 'waiting_approval': return 0.75;
      case 'completed': return 1;
      default: return 0;
    }
  };

  // Add a new state for image loading status
  const [loadedImages, setLoadedImages] = React.useState<Record<string, string>>({});

  // Add a function to handle image loading with fallback
  const handleImageLoad = async (fileName: string, photoId: number) => {
    try {
      const imageUrl = getImageUrl(fileName);
      const resolvedUrl = await loadImageWithFallback(imageUrl);
      setLoadedImages(prev => ({
        ...prev,
        [`${photoId}`]: resolvedUrl
      }));
    } catch (error) {
      console.error('Error loading image:', error);
      // Use a placeholder in case of error
      setLoadedImages(prev => ({
        ...prev,
        [`${photoId}`]: '/placeholder-image.svg'
      }));
    }
  };

  // Load images when issues change
  React.useEffect(() => {
    const loadIssueImages = async () => {
      if (filteredIssues?.length) {
        for (const issue of filteredIssues) {
          if (issue.photos?.length) {
            for (const photo of issue.photos) {
              await handleImageLoad(photo.file_name, photo.id);
            }
          }
        }
      }
    };
    
    loadIssueImages();
  }, [filteredIssues]);

  // Function to convert image URL to base64 with resizing
  const getResizedImageBase64 = async (imageUrl: string): Promise<string> => {
    try {
      // Create a new Image object
      const img = new Image();
      
      // Create a promise to handle the image loading
      const imageLoaded = new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () => reject(new Error('Failed to load image'));
        
        // Set crossOrigin to anonymous to avoid CORS issues when loading images from different domains
        img.crossOrigin = 'anonymous';
      });
      
      // Set the source of the image
      img.src = imageUrl;
      
      // Wait for the image to load
      await imageLoaded;
      
      // Create a canvas element
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      if (!ctx) {
        throw new Error('Failed to get canvas context');
      }
      
      // Set the canvas dimensions to 3cm x 3cm (approximately 113 x 113 pixels at 96 DPI)
      // Excel uses 96 DPI by default
      const size = 113; // 3cm at 96 DPI
      canvas.width = size;
      canvas.height = size;
      
      // Draw the image on the canvas with proper scaling
      ctx.drawImage(img, 0, 0, size, size);
      
      // Convert the canvas to base64 data URL
      return canvas.toDataURL('image/png');
    } catch (error) {
      console.error('Error converting image to base64:', error);
      return ''; // Return empty string if conversion fails
    }
  };

  // Generate the public URL for sharing
  const getPublicUrl = () => {
    return `${window.location.origin}/view/${id}`;
  };

  // Handle copying the URL to clipboard
  const handleCopyUrl = () => {
    try {
      const publicUrl = getPublicUrl();
      navigator.clipboard.writeText(publicUrl)
        .catch((err) => {
          console.error('Failed to copy text: ', err);
          // Fallback method for older browsers
          const textArea = document.createElement('textarea');
          textArea.value = publicUrl;
          document.body.appendChild(textArea);
          textArea.select();
          document.execCommand('copy');
          document.body.removeChild(textArea);
        });
    } catch (err) {
      console.error('Copy failed: ', err);
    }
  };

  if (projectLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="text" width={200} height={40} />
        <Grid container spacing={3} sx={{ mt: 2 }}>
          <Grid item xs={12} md={8}>
            <Skeleton variant="rectangular" height={200} />
          </Grid>
          <Grid item xs={12} md={4}>
            <Skeleton variant="rectangular" height={200} />
          </Grid>
        </Grid>
      </Box>
    );
  }

  if (projectError) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">{projectError}</Typography>
      </Box>
    );
  }

  if (!currentProject) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Project not found</Typography>
      </Box>
    );
  }

  const formatDate = (date: Date) => {
    const months = [
        "Jan", "Feb", "Mar", "Apr", "May", "Jun",
        "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
    ];

    const day = date.getDate(); 
    const month = months[date.getMonth()]; 
    const year = date.getFullYear(); 

    const formattedDay = day < 10 ? `0${day}` : day;

    return `${formattedDay}-${month}-${year}`;
  }

  const formatWord = (word: string) => {
    return word
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  return (
    <Box sx={{ p: 3 }}>
      <Breadcrumbs 
        items={[
          { label: 'Events', path: '/projects' },
          { label: currentProject.name }
        ]} 
      />

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <IconButton onClick={() => navigate('/projects')} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" component="h1" sx={{ flexGrow: 1 }}>
          { currentProject?.code || 'Loading...'}
        </Typography>
        <Button
          variant="outlined"
          color="primary"
          onClick={() => setShareDialogOpen(true)}
        >
          Share
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* Project Details */}
        <Grid item xs={12} md={12}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Event Details
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <Typography variant="subtitle2" color="text.secondary">
                  Name
                </Typography>
                <Typography>
                  {currentProject.name || 'No name provided'}
                </Typography>
              </Grid>
            
              <Grid item xs={4} sm={2}>
                <Typography variant="subtitle2" color="text.secondary">
                    Lines
                </Typography>
                <Typography variant="h4">
                  {currentProject.lines_count}
                </Typography>
              </Grid>
              <Grid item xs={4} sm={2}>
                <Typography variant="subtitle2" color="text.secondary">
                  Issues
                </Typography>
                <Typography variant="h4">
                  {currentProject.issues_count}
                </Typography>
              </Grid>
              <Grid item xs={4} sm={2}>
                <Typography variant="subtitle2" color="text.secondary">
                  Created On
                </Typography>
                <Typography>
                  {formatDate(new Date(currentProject.created_on))}
                </Typography>
              </Grid>
              <Grid item xs={4} sm={3}>
                <div style={{ textAlign: 'right' }}>
                  <Button
                    variant="contained"
                    onClick={handleLinesClick}
                    sx={{ mr: 2 }}
                  >
                    LINES
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleDataAnalysisClick}
                  >
                    DATA ANALYSIS
                  </Button>
                </div>
              </Grid>
            </Grid>
          </Paper>

          {/* Issues Table */}
          <Paper sx={{ p: 3, mt: 3 }}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6">
                BIRA SUMMARY
                {filterApplied && (
                  <Chip 
                    label="Filtered" 
                    color="primary" 
                    size="small" 
                    sx={{ ml: 1 }} 
                    onDelete={clearAppliedFilter}
                  />
                )}
              </Typography>
              
              {/* Controls Row */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                {/* Left side: Sort and Search */}
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flex: 1 }}>
                  <TextField
                    select
                    label="Sort By"
                    variant="outlined"
                    size="small"
                    value={orderBy}
                    onChange={(e) => {
                      setOrderBy(e.target.value as OrderBy);
                    }}
                    sx={{ width: 150 }}
                  >
                    <MenuItem value="id">No</MenuItem>
                    <MenuItem value="type">Type of Issue</MenuItem>
                    <MenuItem value="rank">Rank</MenuItem>
                    <MenuItem value="completion_date">Due Date</MenuItem>
                    <MenuItem value="status">Status</MenuItem>
                    <MenuItem value="progress">Progress</MenuItem>
                  </TextField>
                  <TextField
                    select
                    label="Order"
                    variant="outlined"
                    size="small"
                    value={order}
                    onChange={(e) => {
                      setOrder(e.target.value as Order);
                    }}
                    sx={{ width: 150 }}
                  >
                    <MenuItem value="asc">Ascending</MenuItem>
                    <MenuItem value="desc">Descending</MenuItem>
                  </TextField>
                  
                  {/* Search field - always visible */}
                  <TextField
                    placeholder="Search BIRA..."
                    variant="outlined"
                    size="small"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    sx={{ width: '300px' }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                  
                  {/* Filter selection info and buttons - only when filter mode is active */}
                  {showFilters && (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                        {selectedIssues.length} of {filteredIssues.length} rows selected
                      </Typography>
                      <Button 
                        variant="outlined" 
                        size="small" 
                        onClick={() => setSelectedIssues([])}
                        disabled={selectedIssues.length === 0}
                        sx={{ mr: 1 }}
                      >
                        Clear Selection
                      </Button>
                      <Button 
                        variant="contained" 
                        color="primary"
                        size="small"
                        onClick={() => setFilterApplied(true)}
                        disabled={selectedIssues.length === 0 || filterApplied}
                      >
                        Apply Filter
                      </Button>
                    </Box>
                  )}
                </Box>
                
                {/* Right side: Action buttons */}
                <Box>
                  <Tooltip title="Export to Excel">
                    <IconButton 
                      color="primary" 
                      onClick={exportToExcelWithExcelJS}
                      sx={{ mr: 1 }}
                    >
                      <FileDownloadIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={showFilters ? "Exit Filter Mode" : "Filter Mode"}>
                    <IconButton
                      color={showFilters ? "secondary" : "default"}
                      onClick={toggleFilters}
                      sx={{ mr: 1 }}
                    >
                      <FilterListIcon />
                    </IconButton>
                  </Tooltip>
                  {filterApplied && (
                    <Tooltip title="Clear Applied Filter">
                      <IconButton
                        color="error"
                        onClick={clearAppliedFilter}
                        sx={{ mr: 1 }}
                      >
                        <CloseIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Button
                    startIcon={<AddIcon />}
                    variant="contained"
                    onClick={handleCreateIssueClick}
                  >
                    New BIRA
                  </Button>
                </Box>
              </Box>
            </Box>

            <TableContainer>
              <Table>
                <TableHead sx={{ backgroundColor: '#e0e0e0' }}>
                  <TableRow>
                  {/* Apply text color to all header cells */}
                    {showFilters && (
                      <TableCell padding="checkbox">
                        <Checkbox
                          indeterminate={selectedIssues.length > 0 && selectedIssues.length < filteredIssues.length}
                          checked={selectAll}
                          onChange={handleSelectAll}
                          
                        />
                      </TableCell>
                    )}
                    <TableCell>No</TableCell>
                    <TableCell>Type of Issue</TableCell>
                    <TableCell>Rank</TableCell>
                    <TableCell>Line</TableCell>
                    <TableCell>Part No & Name</TableCell>
                    <TableCell>Problem Description</TableCell>
                    <TableCell>Picture</TableCell>
                    <TableCell>Counter Measure Request</TableCell>
                    <TableCell>Final Counter Measure</TableCell>
                    <TableCell>Issued By & Date</TableCell>
                    <TableCell>C/M PIC</TableCell>
                    <TableCell>Reviewer</TableCell>
                    <TableCell>Approver</TableCell>
                    <TableCell>Due Date</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Progress</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {issuesLoading ? (
                    // Show skeleton rows while loading
                    Array.from({ length: rowsPerPage }).map((_, index) => (
                      <TableRow key={`skeleton-${index}`}>
                        {showFilters && <TableCell><Skeleton /></TableCell>}
                        <TableCell><Skeleton /></TableCell>
                        <TableCell><Skeleton /></TableCell>
                        <TableCell><Skeleton /></TableCell>
                        <TableCell><Skeleton /></TableCell>
                        <TableCell><Skeleton /></TableCell>
                        <TableCell><Skeleton /></TableCell>
                        <TableCell><Skeleton /></TableCell>
                        <TableCell><Skeleton /></TableCell>
                        <TableCell><Skeleton /></TableCell>
                        <TableCell><Skeleton /></TableCell>
                        <TableCell><Skeleton /></TableCell>
                        <TableCell><Skeleton /></TableCell>
                        <TableCell><Skeleton /></TableCell>
                        <TableCell><Skeleton /></TableCell>
                        <TableCell><Skeleton /></TableCell>
                        <TableCell><Skeleton /></TableCell>
                      </TableRow>
                    ))
                  ) : paginatedIssues.map((issue) => (
                    <TableRow 
                      key={issue.id}
                      hover
                      selected={selectedIssues.includes(issue.id)}
                      sx={{ 
                        cursor: 'pointer',
                        '&:hover': {
                          bgcolor: 'action.hover',
                        },
                      }}
                    >
                      {showFilters && (
                        <TableCell 
                          padding="checkbox" 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelectRow(issue.id, e);
                          }}
                        >
                          <Checkbox
                            checked={selectedIssues.includes(issue.id)}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => {
                              e.stopPropagation();
                              handleSelectRow(issue.id, e as unknown as React.MouseEvent<HTMLElement>);
                            }}
                          />
                        </TableCell>
                      )}
                      <TableCell onClick={() => navigateToIssueDetail(issue.id)}>{issue.id}</TableCell>
                      <TableCell onClick={() => navigateToIssueDetail(issue.id)}>{formatWord(issue.type)}</TableCell>
                      <TableCell onClick={() => navigateToIssueDetail(issue.id)}>{formatWord(issue.rank)}</TableCell>
                      <TableCell onClick={() => navigateToIssueDetail(issue.id)}>{issue.line_name}</TableCell>
                      <TableCell onClick={() => navigateToIssueDetail(issue.id)}>{issue.part_number}<br/>{issue.part_name}</TableCell>
                      <TableCell onClick={() => navigateToIssueDetail(issue.id)}>{issue.problem}</TableCell>
                      <TableCell onClick={() => navigateToIssueDetail(issue.id)} sx={{ verticalAlign: 'top' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {issue.photos?.slice(0, 2).map((photo) => (
                            <Box
                              key={photo.id}
                              component="img"
                              src={loadedImages[`${photo.id}`] || getImageUrl(photo.file_name)}
                              alt={`Issue photo ${photo.id}`}
                              sx={{
                                width: 40,
                                height: 40,
                                objectFit: 'cover',
                                borderRadius: 1,
                                cursor: 'pointer',
                              }}
                              onError={(e) => {
                                // Fallback to placeholder if image fails to load
                                const target = e.target as HTMLImageElement;
                                target.onerror = null; // Prevent infinite loop
                                target.src = '/placeholder-image.svg';
                              }}
                              onClick={() => setSelectedImage(loadedImages[`${photo.id}`] || getImageUrl(photo.file_name))}
                            />
                          ))}
                          {issue.photos && issue.photos.length > 2 && (
                            <Box
                              sx={{
                                width: 40,
                                height: 40,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: 'grey.200',
                                borderRadius: 1,
                                typography: 'caption',
                                cursor: 'pointer',
                              }}
                              onClick={() => {
                                // Show first photo when clicking +N
                                if (issue.photos && issue.photos.length > 0) {
                                  setSelectedImage(getImageUrl(issue.photos[0].file_name));
                                }
                              }}
                            >
                              +{issue.photos.length - 2}
                            </Box>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell onClick={() => navigateToIssueDetail(issue.id)}>{issue.counter_measure}</TableCell>
                      <TableCell onClick={() => navigateToIssueDetail(issue.id)}>{issue.final_counter_measure}</TableCell>
                      <TableCell onClick={() => navigateToIssueDetail(issue.id)}>{issue.created_by_name}<br/>{formatDate(new Date(issue.created_on))}</TableCell>
                      <TableCell onClick={() => navigateToIssueDetail(issue.id)}>{issue.pic_name || ""}</TableCell>
                      <TableCell onClick={() => navigateToIssueDetail(issue.id)}>{issue.reviewer_pic_name || ""}</TableCell>
                      <TableCell onClick={() => navigateToIssueDetail(issue.id)}>{issue.approver_pic_name || ""}</TableCell>
                      <TableCell onClick={() => navigateToIssueDetail(issue.id)}>{issue.completion_date ? formatDate(new Date(issue.completion_date)) : ""}</TableCell>
                      <TableCell onClick={() => navigateToIssueDetail(issue.id)}>
                        {formatWord(issue.status)}
                      </TableCell>
                      <TableCell align="center" onClick={() => navigateToIssueDetail(issue.id)}>
                        <Box sx={{ position: 'relative', width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {(() => {
                            const percentage = getStatusPercentage(issue.status);
                            let imageName = 'progress-25.png';
                            
                            if (percentage <= 0) {
                              // For 0% progress, just show the text without an image
                              return (
                                <Box 
                                  sx={{ 
                                    position: 'relative', 
                                    width: '40px', 
                                    height: '40px',
                                    border: '1px solid #ccc',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontSize: '10px',
                                      fontWeight: 'bold'
                                    }}
                                  >
                                    0%
                                  </Typography>
                                </Box>
                              );
                            } else if (percentage <= 0.25) {
                              imageName = 'progress-25.png';
                            } else if (percentage <= 0.5) {
                              imageName = 'progress-50.png';
                            } else if (percentage <= 0.75) {
                              imageName = 'progress-75.png';
                            } else {
                              imageName = 'progress-100.png';
                            }
                            
                            return (
                              <Box sx={{ position: 'relative', width: '40px', height: '40px' }}>
                                <img 
                                  src={`${process.env.PUBLIC_URL}/${imageName}`} 
                                  alt={`${Math.round(percentage * 100)}% complete`}
                                  style={{ width: '100%', height: '100%' }}
                                />
                              </Box>
                            );
                          })()}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!issuesLoading && filteredIssues.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={showFilters ? 17 : 16} align="center">
                        No issues found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={filteredIssues.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Paper>
        </Grid>
      </Grid>

      {/* QR Code Share Dialog */}
      <Dialog 
        open={shareDialogOpen} 
        onClose={() => setShareDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Share Project</Typography>
            <IconButton edge="end" onClick={() => setShareDialogOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box 
            display="flex" 
            flexDirection="column" 
            alignItems="center" 
            justifyContent="center" 
            p={2}
          >
            <Typography variant="body1" gutterBottom>
              Scan this QR code to open on mobile device
            </Typography>
            <Box 
              sx={{ 
                mt: 2, 
                mb: 3, 
                p: 2, 
                border: '1px solid #eee', 
                borderRadius: 1,
                backgroundColor: 'white' 
              }}
            >
              <QrCode value={getPublicUrl()} size={200} />
            </Box>
            <TextField
              fullWidth
              variant="outlined"
              value={getPublicUrl()}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title="Copy to clipboard">
                      <IconButton 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCopyUrl();
                        }} 
                        edge="end"
                      >
                        <ContentCopyIcon />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShareDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Dialogs */}
      <ProjectFormDialog
        open={projectFormOpen}
        onClose={() => setProjectFormOpen(false)}
        onSubmit={handleProjectFormSubmit}
        project={currentProject}
      />

      {/* Image Dialog */}
      <Dialog
        open={!!selectedImage}
        onClose={() => setSelectedImage(null)}
        maxWidth="lg"
        fullWidth
      >
        <DialogContent sx={{ p: 0, position: 'relative' }}>
          {selectedImage && (
            <>
              <IconButton
                onClick={() => setSelectedImage(null)}
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: 8,
                  bgcolor: 'background.paper',
                  '&:hover': {
                    bgcolor: 'background.paper',
                  },
                }}
              >
                <CloseIcon />
              </IconButton>
              <Box
                component="img"
                src={selectedImage}
                alt="Full size"
                sx={{
                  width: '100%',
                  height: 'auto',
                  maxHeight: '90vh',
                  objectFit: 'contain',
                }}
              />
            </>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};
